import { type FC, memo, useEffect, useMemo, useState } from 'react';
import {
  notification,
  Button,
  Collapse,
  Form,
  Skeleton,
  Typography,
} from 'antd';
import type { FormProps } from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import Blocks from '../Blocks/Blocks';
import blockStyles from '../Blocks/style.module.css';
import type { SipAccount } from '../../models/RoboCall/SipAccount';
import {
  getSipAccounts,
  selectSipAccountsSaving,
  selectSipAccounts,
  sipAccountsStatus,
  updateSipAccounts,
} from '../../store/reducers/SIPAccountSlice';
import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  buildSipAccountSaveRequest,
  getAccountAddress,
  getAccountTitle,
  getInitialFormValues,
  type SipAccountFormValues,
} from './sipAccountFormValues';
import { createSipAccountPanels } from './sipAccountPanels';
import styles from './style.module.css';

const { Title, Text } = Typography;
const DEFAULT_OPEN_PANELS = ['basic', 'network', 'registration', 'identity'];
const normalizeCollapseKeys = (keys: string | string[]): string[] =>
  Array.isArray(keys) ? keys : [keys];

export const SIPAccountDetail: FC = memo(() => {
  const [form] = Form.useForm<SipAccountFormValues>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const status = useAppSelector(sipAccountsStatus);
  const saving = useAppSelector(selectSipAccountsSaving);
  const sipAccounts = useAppSelector(selectSipAccounts);
  const [activePanels, setActivePanels] =
    useState<string[]>(DEFAULT_OPEN_PANELS);
  const [serverRequiredVisible, setServerRequiredVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const actionPending =
    status.responseLoading || saving || submitting || deleting;

  useEffect(() => {
    if (sipAccounts.length === 0) {
      dispatch(getSipAccounts());
    }
  }, [dispatch, sipAccounts.length]);

  const account = useMemo(
    () => sipAccounts.find((item) => item.id === id),
    [id, sipAccounts],
  );

  const isCreateMode = id === undefined;
  const title = isCreateMode
    ? 'Новый SIP-аккаунт'
    : account && getAccountTitle(account);
  const subtitle = 'Настройка параметров аккаунта';
  const accountAddress = account ? getAccountAddress(account) : '';
  const showDefaultBadge = Boolean(account?.default);
  const showDeleteButton = Boolean(account && !account.default);

  useEffect(() => {
    form.setFieldsValue(
      getInitialFormValues(account, isCreateMode, sipAccounts.length),
    );
    setServerRequiredVisible(false);
  }, [account?.id, form, id, isCreateMode, sipAccounts.length]);

  const handleBack = () => {
    navigate('/sip-accounts');
  };

  const showSaveError = (error: unknown) => {
    const description =
      typeof error === 'string'
        ? error
        : error instanceof Error
          ? error.message
          : 'Не удалось сохранить аккаунты!';

    notification.error({
      message: 'Не удалось сохранить SIP-аккаунты',
      description,
    });
  };

  const getLatestSipAccounts = async (): Promise<SipAccount[]> => {
    try {
      return await dispatch(getSipAccounts()).unwrap();
    } catch {
      return sipAccounts;
    }
  };

  const handleSubmit: FormProps<SipAccountFormValues>['onFinish'] = (
    values,
  ) => {
    if (actionPending) return;
    setServerRequiredVisible(false);

    void (async () => {
      try {
        setSubmitting(true);
        const latestAccounts = await getLatestSipAccounts();
        await dispatch(
          updateSipAccounts(
            buildSipAccountSaveRequest(
              values,
              latestAccounts,
              account,
              isCreateMode,
            ).sipAccounts,
          ),
        ).unwrap();
        navigate('/sip-accounts', { state: { skipInitialRefresh: true } });
      } catch (error) {
        showSaveError(error);
      } finally {
        setSubmitting(false);
      }
    })();
  };

  const handleSubmitFailed: FormProps<SipAccountFormValues>['onFinishFailed'] =
    (errorInfo) => {
      setServerRequiredVisible(
        errorInfo.errorFields.some((field) => field.name.includes('server')),
      );
    };

  const handleValuesChange: FormProps<SipAccountFormValues>['onValuesChange'] =
    (changedValues) => {
      if (Object.prototype.hasOwnProperty.call(changedValues, 'server')) {
        setServerRequiredVisible(false);
      }
    };

  const handleDelete = () => {
    if (!account || account.default || actionPending) {
      return;
    }

    void (async () => {
      try {
        setDeleting(true);
        const latestAccounts = await getLatestSipAccounts();
        const accountsWithoutCurrent = latestAccounts.filter(
          (item) => item.id !== account.id,
        );
        await dispatch(updateSipAccounts(accountsWithoutCurrent)).unwrap();
        navigate('/sip-accounts');
      } catch (error) {
        showSaveError(error);
      } finally {
        setDeleting(false);
      }
    })();
  };

  const collapseItems = createSipAccountPanels({
    isCreateMode,
    serverRequiredVisible,
  });

  return (
    <Blocks
      topBlockClassName={styles.stickyTop}
      topBlock={
        <div className={blockStyles.detailHeader}>
          <div className={styles.detailTopLeft}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              className={blockStyles.detailBackButton}
              aria-label="Вернуться к списку SIP-аккаунтов"
              disabled={actionPending}
            />
            <div className={styles.detailTitleBlock}>
              {status.responseLoading ? (
                <Skeleton.Input active className={styles.detailTitleSkeleton} />
              ) : (
                <div
                  className={styles.detailTitleRow}
                  data-testid="sip-detail-title"
                >
                  <Title level={4} className={styles.detailTitle}>
                    {title ?? 'SIP-аккаунт не найден'}
                  </Title>
                  {showDefaultBadge ? (
                    <span className={styles.defaultBadge}>По умолчанию</span>
                  ) : null}
                </div>
              )}
              {status.responseLoading ? null : (
                <>
                  <Text type="secondary" className={styles.detailSubtitle}>
                    {subtitle ?? 'Настройка параметров аккаунта'}
                    {accountAddress ? ` · ${accountAddress}` : ''}
                  </Text>
                </>
              )}
            </div>
          </div>

          <div className={blockStyles.detailActions}>
            {showDeleteButton ? (
              <Button
                danger
                icon={<DeleteOutlined />}
                className={blockStyles.detailDangerButton}
                aria-label="Удалить SIP-аккаунт"
                onClick={handleDelete}
                loading={deleting}
                disabled={actionPending}
              >
                Удалить
              </Button>
            ) : null}
            <Button
              type="primary"
              icon={<SaveOutlined />}
              className={blockStyles.detailPrimaryButton}
              aria-label="Сохранить SIP-аккаунт"
              onClick={() => form.submit()}
              loading={saving || submitting}
              disabled={actionPending}
            >
              Сохранить
            </Button>
          </div>
        </div>
      }
      mainBlock={
        status.responseLoading ? (
          <div className={styles.skeletonWrap}>
            <Skeleton active paragraph={{ rows: 8 }} />
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            className={styles.detailForm}
            onFinish={handleSubmit}
            onFinishFailed={handleSubmitFailed}
            onValuesChange={handleValuesChange}
            disabled={actionPending}
          >
            <div className={styles.detailMainBlock}>
              <Collapse
                data-testid="sip-account-collapse"
                activeKey={activePanels}
                onChange={(keys) =>
                  setActivePanels(normalizeCollapseKeys(keys))
                }
                className={styles.collapse}
                expandIconPosition="end"
                items={collapseItems}
              />
            </div>
          </Form>
        )
      }
    />
  );
});

SIPAccountDetail.displayName = 'SIPAccountDetail';

export default SIPAccountDetail;
